import { Switch } from '../../components/ui/switch';
import { Atom, Flask, Calculator, PercentIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import StatCell from '../generic/StatCell';
import { secondsToMinutes } from '../../../../../apps/frontend/src/utils/utilFunctions';
import moment from 'moment';
import TableHeader from '../../components/generic/TableHeader';
import TimeAnalysisSkeleton from './TimeAnalysisHourSkeleton';
import NoData from '../generic/NoData';
import { useNavigate } from 'react-router-dom';

const TimeAnalysisSubjectTestBreakdown = ({ data, loading, summary, onResetFilters }) => {
  const [showPercentage, setShowPercentage] = useState(true);
  const navigate = useNavigate();

  if (loading) return <TimeAnalysisSkeleton />;
  if (!data || data.length === 0) return <NoData onResetAll={onResetFilters} />;

  // Get subjects from summary
  const subjects = summary
    ? Object.keys(summary).map((key) => ({
        title: summary[key]?.subjectMeta?.title,
        icon: summary[key]?.subjectMeta?.icon,
        color: summary[key]?.subjectMeta?.color,
      }))
    : [];

  const icons = {
    Physics: Atom,
    Chemistry: Flask,
    Mathematics: Calculator,
  };

  return (
    <div>
      <TableHeader
        headerTitle="Subject & Test-wise Time Spent"
        headerSwitch={
          <span className="flex items-center gap-1">
            <PercentIcon size={16} weight="bold" />
            Show Percentage
          </span>
        }
        headerSwitchCase={showPercentage}
        headerSwitchHandler={setShowPercentage}
      >
        {/* Mobile scroll container */}
        <div className="overflow-x-auto">
          <div className="min-w-[700px] border border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">
            {/* Table Header Row */}
            <div className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)]">
              <div
                className={`grid divide-x divide-[var(--q3-stroke-light)] text-sm font-medium text-[var(--q3-neutral-default)]`}
                style={{ gridTemplateColumns: `220px repeat(${subjects.length}, minmax(0, 1fr))` }}
              >
                {/* Test Title Header */}
                <div className="p-3 flex items-center justify-start max-w-[220px]">Test Title</div>

                {/* Subject Headers */}
                {subjects.map((subject, index) => (
                  <div
                    key={index}
                    className="p-3 text-center flex items-center justify-center min-w-[80px] gap-1"
                    style={{ color: subject.color }}
                  >
                    {subject.icon && <img src={subject.icon} alt={subject.title} className="w-4 h-4" />}
                    <span className="text-xs truncate">{subject.title}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Table Body */}
            {data.map((test, index) => (
              <div key={index} className="border-b border-[var(--q3-stroke-light)] last:border-b-0">
                <div
                  className={`grid divide-x divide-[var(--q3-stroke-light)] items-center text-sm text-[var(--q3-neutral-default)]`}
                  style={{ gridTemplateColumns: `220px repeat(${subjects.length}, minmax(0, 1fr))` }}
                >
                  {/* Test Title Cell (Clickable) */}
                  <div
                    className="p-3 h-full flex flex-col items-start justify-center cursor-pointer hover:bg-[var(--q3-surface-hover)] max-w-[220px]"
                    onClick={() => navigate(`/${test.packId}/${test.testId}/analysis`)}
                  >
                    <div className="font-medium text-[var(--q3-neutral-default)] truncate max-w-[190px]">
                      {test.testTitle}
                    </div>
                    <div className="text-xs text-[var(--q3-neutral-light)]">
                      {moment(test.submittedAt).format('D MMM')}
                    </div>
                  </div>

                  {/* Subject-wise Time Cells */}
                  {subjects.map((subject, subjIndex) => {
                    const subjectData = test.subjectWiseTimeSpent?.find(
                      (sub) => sub.subjectMeta?.title === subject.title,
                    );

                    return (
                      <div key={subjIndex} className="text-center">
                        {subjectData ? (
                          <StatCell
                            color={subject.color}
                            top={secondsToMinutes(subjectData.totalTimeSpent)}
                            bottom={secondsToMinutes(test.totalTimeSpent)}
                            showPercentage={showPercentage}
                            textAppend="min"
                            compact={true}
                          />
                        ) : (
                          <span className="text-[var(--q3-neutral-light)] text-xs">-</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </TableHeader>
    </div>
  );
};

export default TimeAnalysisSubjectTestBreakdown;
